import { useState, useEffect, useMemo, useCallback } from "react";
import { StickyNote, Lock, ReceiptText, Save, Loader2, AlertCircle, Info } from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import { useUpdateBookingNotes, useUploadBookingNoteImage } from "../../../api/booking/useBookingApi";
import RichTextEditor from "../../../components/Booking/RichTextEditor";
import styles from "./NotesTab.module.css";

/* Booking-level Notes tab — two independent rich-text fields:
   • Notes for Office   → internal only, never shown to the customer
   • Notes on Invoice   → rendered on the customer invoice
   Each editor supports formatting, bullet points and image thumbnails (OVH). */

const NotesTab = ({ bookingId, officeNotes = "", invoiceNotes = "", isLocked = false, onSaved }) => {
  const { showToast } = useToast();
  const { execute, loading: saving } = useUpdateBookingNotes();
  const { upload, uploading } = useUploadBookingNoteImage();

  const [office, setOffice] = useState(officeNotes || "");
  const [invoice, setInvoice] = useState(invoiceNotes || "");

  // Re-sync when the saved values change (e.g. after a refetch). Local edits are
  // only overwritten when the incoming saved value actually changes.
  useEffect(() => { setOffice(officeNotes || ""); }, [officeNotes]);
  useEffect(() => { setInvoice(invoiceNotes || ""); }, [invoiceNotes]);

  const dirty = useMemo(
    () => office !== (officeNotes || "") || invoice !== (invoiceNotes || ""),
    [office, invoice, officeNotes, invoiceNotes]
  );

  const handleUpload = useCallback(async (file) => {
    if (!bookingId) return null;
    const res = await upload(bookingId, file);
    if (res.success) {
      showToast("Image uploaded", "success");
      return res.imageUrl;
    }
    showToast(res.message || "Image upload failed", "error");
    return null;
  }, [bookingId, upload, showToast]);

  const handleSave = async () => {
    if (!bookingId || saving || !dirty) return;
    const res = await execute(bookingId, { officeNotes: office, invoiceNotes: invoice });
    if (res.success) {
      showToast("Notes saved successfully", "success");
      onSaved?.();
    } else {
      showToast(res.message || "Failed to save notes", "error");
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.bar}>
        <div className={styles.barTitle}>
          <StickyNote size={16} /> Notes
          {isLocked && <span className={styles.lockedBadge}><Lock size={11} /> Locked</span>}
        </div>
        <div className={styles.barActions}>
          {dirty && !isLocked && <span className={styles.dirty}><AlertCircle size={12} /> Unsaved changes</span>}
          <button
            type="button"
            className={styles.saveBtn}
            disabled={!dirty || saving || isLocked}
            onClick={handleSave}
          >
            {saving ? <Loader2 size={13} className={styles.spin} /> : <Save size={13} />}
            {saving ? "Saving…" : "Save Notes"}
          </button>
        </div>
      </div>

      <div className={styles.grid}>
        {/* Office notes */}
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>
              <span className={`${styles.cardIcon} ${styles.cardIconOffice}`}><Lock size={15} /></span>
              <div>
                <div className={styles.cardName}>Notes for Office</div>
                <div className={styles.cardDesc}>Internal staff notes — never shown to the customer or on documents.</div>
              </div>
            </div>
            <span className={`${styles.tag} ${styles.tagInternal}`}>Internal only</span>
          </div>
          <RichTextEditor
            value={office}
            onChange={setOffice}
            onUploadImage={handleUpload}
            disabled={isLocked}
            placeholder="Add internal notes — use the toolbar for headings, bullet points, links and image thumbnails…"
          />
        </section>

        {/* Invoice notes */}
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>
              <span className={`${styles.cardIcon} ${styles.cardIconInvoice}`}><ReceiptText size={15} /></span>
              <div>
                <div className={styles.cardName}>Notes — Visible on Invoice</div>
                <div className={styles.cardDesc}>Printed on the customer invoice. Keep them clear and customer-friendly.</div>
              </div>
            </div>
            <span className={`${styles.tag} ${styles.tagInvoice}`}>Shown on invoice</span>
          </div>
          <RichTextEditor
            value={invoice}
            onChange={setInvoice}
            onUploadImage={handleUpload}
            disabled={isLocked}
            placeholder="Notes that will appear on the invoice — terms, remarks, payment details…"
          />
        </section>
      </div>

      <div className={styles.footNote}>
        <Info size={13} />
        <span>
          Office notes stay internal. Invoice notes are rendered on the customer invoice the next time it is generated.
          {uploading ? " · Uploading image…" : ""}
        </span>
      </div>
    </div>
  );
};

export default NotesTab;
